/** DOM dialogue/controls for the Three.js diorama cutscenes.
 * The game renderer owns all story visuals; this component owns subtitles, voices and progression. */
import type { Beat, Character, Cutscene } from '../data/cutscenes'
import type { LipSyncTrack } from '../audio/CutsceneLipSync'
import { CutsceneSound } from '../audio/CutsceneSound'
import { CutsceneVoice } from '../audio/CutsceneVoice'
import { cutsceneVoiceClipId, verifiedCutsceneVoiceClipUrl } from '../audio/cutscene-voice-assets'
import { DioramaStage, type DioramaAssetState } from '../cutscenes/DioramaStage'
import lipSyncData from '../data/fish-cutscene-lipsync.json'
import { diag } from '../utils/diagnostics'

const TYPE_CPS = 45
const READ_HOLD = 1.05
const ACTION_HOLD = 2.6
const LIP_SYNC = lipSyncData as Record<string, LipSyncTrack>

const NAMES: Record<Character, string> = {
  bluebob: 'BLUEBOB',
  krabs: 'MONEY KRABS',
  king: 'JELLY KING',
  zap: 'ZAP',
}

export class CutsceneScreen {
  readonly el: HTMLDivElement
  readonly stage: DioramaStage
  private nameEl: HTMLDivElement
  private textEl: HTMLDivElement
  private hintEl: HTMLDivElement
  private titleEl: HTMLDivElement
  private dialogueEl: HTMLDivElement
  private progressEl: HTMLDivElement
  private loadingEl: HTMLDivElement
  private titleCardsEl: HTMLDivElement
  private skipBtn!: HTMLButtonElement
  private voiceBtn!: HTMLButtonElement
  private voice = new CutsceneVoice()
  private sound = new CutsceneSound()
  private shownVoiceSignature = ''
  private shownDetailSignature = ''

  private cs: Cutscene | null = null
  private beatIdx = 0
  private onDone: (() => void) | null = null
  private beatElapsed = 0
  private beatDuration = ACTION_HOLD
  private beatWallElapsed = 0
  private lastUpdateTime = 0
  private waitingForAssets = false
  private disposed = false
  private textRevealed = false
  private playing = false
  private reduceMotion = false

  constructor() {
    this.reduceMotion = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches

    this.el = document.createElement('div')
    this.el.className = 'cutscene is-hidden'
    this.el.style.display = 'none'
    this.el.dataset.testid = 'cutscene'
    this.el.dataset.renderer = 'three-diorama'
    this.el.dataset.assetsReady = 'false'
    this.el.setAttribute('role', 'dialog')
    this.el.setAttribute('aria-modal', 'true')
    this.el.setAttribute('aria-label', 'Story cutscene')

    this.stage = new DioramaStage((state) => this.onAssetsChanged(state))
    this.stage.setReducedMotion(this.reduceMotion)
    if (import.meta.env.DEV) diag().cutsceneStage = {
      animationSnapshot: () => this.stage.animationSnapshot(),
      collisionSnapshot: () => this.stage.collisionSnapshot(),
      collisionCorrections: () => this.stage.collisionCorrections(),
      collisionIssues: () => this.stage.collisionIssues(),
    }

    const header = document.createElement('div')
    header.className = 'cutscene-header'
    const heading = document.createElement('div')
    heading.className = 'cutscene-heading'
    const kicker = document.createElement('div')
    kicker.className = 'cutscene-kicker'
    kicker.textContent = 'BLUEBOB STORY'
    this.titleEl = document.createElement('div')
    this.titleEl.className = 'cutscene-title'
    heading.append(kicker, this.titleEl)

    const actions = document.createElement('div')
    actions.className = 'cutscene-actions'
    const voice = document.createElement('button')
    voice.className = 'cutscene-control cutscene-voice'
    voice.dataset.testid = 'cutscene-voice'
    voice.addEventListener('click', (event) => {
      event.stopPropagation()
      const muted = this.voice.toggleMuted()
      this.sound.setMuted(muted)
      this.updateVoiceButton(true)
    })
    this.voiceBtn = voice

    const skip = document.createElement('button')
    skip.textContent = 'SKIP  ⏭'
    skip.className = 'cutscene-control cutscene-skip'
    skip.addEventListener('click', (event) => {
      event.stopPropagation()
      this.finish()
    })
    this.skipBtn = skip
    actions.append(voice, skip)
    header.append(heading, actions)
    this.el.appendChild(header)

    this.loadingEl = document.createElement('div')
    this.loadingEl.className = 'cutscene-loading'
    this.loadingEl.textContent = 'SETTING THE SCENE…'
    this.el.appendChild(this.loadingEl)

    this.titleCardsEl = document.createElement('div')
    this.titleCardsEl.className = 'cutscene-title-cards is-hidden'
    this.titleCardsEl.setAttribute('aria-live', 'polite')
    this.el.appendChild(this.titleCardsEl)

    const box = document.createElement('div')
    box.className = 'cutscene-dialogue'
    this.dialogueEl = box
    this.nameEl = document.createElement('div')
    this.nameEl.className = 'cutscene-speaker'
    this.textEl = document.createElement('div')
    this.textEl.className = 'cutscene-line'
    this.textEl.setAttribute('aria-live', 'polite')
    const footer = document.createElement('div')
    footer.className = 'cutscene-dialogue-footer'
    this.progressEl = document.createElement('div')
    this.progressEl.className = 'cutscene-progress'
    this.hintEl = document.createElement('div')
    this.hintEl.className = 'cutscene-hint'
    this.hintEl.textContent = 'TAP OR PRESS SPACE  ▸'
    footer.append(this.progressEl, this.hintEl)
    box.append(this.nameEl, this.textEl, footer)
    this.el.appendChild(box)

    this.el.addEventListener('click', () => this.onTap())
    window.addEventListener('keydown', this.onKey)
    this.updateVoiceButton(true)
  }

  private onKey = (event: KeyboardEvent): void => {
    if (!this.playing) return
    if (event.repeat) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }
    if (event.code === 'Space' || event.code === 'Enter') {
      event.preventDefault()
      event.stopImmediatePropagation()
      if (document.activeElement === this.skipBtn) this.finish()
      else if (document.activeElement === this.voiceBtn) this.voiceBtn.click()
      else this.onTap()
    } else if (event.code === 'Escape') {
      event.stopImmediatePropagation()
      this.finish()
    }
  }

  /** Begin a story sequence. GameApp switches the shared renderer to `stage.scene/camera`. */
  play(cs: Cutscene, onDone: () => void): void {
    if (this.disposed) return
    if (this.playing) this.finish()
    this.cs = cs
    this.onDone = onDone
    this.beatIdx = 0
    this.playing = true
    this.titleEl.textContent = cs.title
    this.el.classList.remove('is-hidden')
    this.el.style.display = 'block'
    this.el.dataset.assetsReady = String(this.stage.assets.ready)
    this.loadingEl.classList.toggle('is-hidden', this.stage.assets.ready)
    diag().cutscene = { id: cs.id, beat: 0, beats: cs.beats.length, playing: true }
    this.waitingForAssets = !this.stage.assets.ready
    this.dialogueEl.classList.add('is-hidden')
    this.titleCardsEl.classList.add('is-hidden')
    if (!this.waitingForAssets) this.startBeat()
  }

  update(dt: number): void {
    if (!this.playing || !this.cs || this.waitingForAssets) return
    const now = performance.now()
    const wallDelta = Math.max(0, (now - this.lastUpdateTime) / 1000)
    this.lastUpdateTime = now
    this.beatWallElapsed += wallDelta
    const beat = this.currentBeat
    const playhead = this.voice.recordedPlayhead
    const canAdvance = !beat.dialogue || this.voice.canAutoAdvance(this.beatWallElapsed, beat.dialogue.speaker, beat.dialogue.text)
    if (playhead !== null && Number.isFinite(playhead)) this.beatElapsed = Math.max(this.beatElapsed, playhead)
    else this.beatElapsed += wallDelta
    const duration = this.beatDuration
    const progress = Math.min(1, this.beatElapsed / duration)
    this.titleCardsEl.classList.toggle('is-hidden', !beat.titleCards?.length
      || (beat.shot === 'ending-5b' && this.beatElapsed < duration - 1.5))
    this.voice.sampleFrameDuration(dt)
    const mouthCue = this.voice.currentMouthCue
    this.el.dataset.actionProgress = progress.toFixed(2)
    this.el.dataset.beatElapsed = this.beatElapsed.toFixed(4)
    this.el.dataset.audioPlayhead = this.voice.recordedPlayhead?.toFixed(4) ?? ''
    this.el.dataset.lipsyncPlayhead = this.voice.lipSyncPlayhead?.toFixed(4) ?? ''
    this.el.dataset.mouthCue = mouthCue ?? (this.voice.isSpeaking ? 'fallback' : 'X')
    this.sound.update(progress)
    this.stage.update(dt, progress, this.beatElapsed, this.voice.isSpeaking, mouthCue)
    this.syncDetailDiagnostics()

    if (!beat.dialogue) {
      if (this.beatElapsed >= (beat.duration ?? ACTION_HOLD)) this.advance()
      return
    }

    const shown = this.typedLen(beat.dialogue.text)
    this.textEl.textContent = beat.dialogue.text.slice(0, shown)
    const done = shown >= beat.dialogue.text.length
    this.hintEl.style.opacity = done ? '1' : '0.25'
    this.updateVoiceButton()

    if (done) {
      const typedSeconds = beat.dialogue.text.length / TYPE_CPS
      const holdElapsed = this.beatElapsed - typedSeconds
      const readTime = READ_HOLD + beat.dialogue.text.length * 0.018
      if (this.beatElapsed >= duration && holdElapsed > readTime
        && canAdvance) this.advance()
    }
  }

  resize(aspect: number): void {
    this.stage.resize(aspect)
  }

  private get currentBeat(): Beat {
    return this.cs!.beats[this.beatIdx]
  }

  private startBeat(): void {
    if (!this.cs) return
    const beat = this.currentBeat
    this.beatElapsed = 0
    this.beatWallElapsed = 0
    this.lastUpdateTime = performance.now()
    this.textRevealed = false
    this.stage.setBeat(beat)
    this.sound.startBeat(beat)
    this.el.dataset.beat = String(this.beatIdx + 1)
    this.el.dataset.shot = beat.shot
    this.el.dataset.scene = beat.scene
    this.el.dataset.speaker = beat.dialogue?.speaker ?? 'action'
    this.el.dataset.action = beat.actions.map((action) => action.type).join(',')
    this.el.dataset.stageActors = beat.cast.join(',')
    this.el.dataset.camera = beat.camera
    this.el.dataset.direction = beat.direction
    this.el.dataset.transition = beat.transition ?? 'cut'
    this.el.dataset.titleCards = String(beat.titleCards?.length ?? 0)
    this.el.dataset.actionProgress = '0.00'
    this.el.dataset.mouthCue = 'X'
    this.syncDetailDiagnostics(true)
    this.dialogueEl.classList.toggle('is-hidden', !beat.dialogue)
    this.nameEl.textContent = beat.dialogue ? NAMES[beat.dialogue.speaker] : ''
    this.progressEl.textContent = `${this.beatIdx + 1}  /  ${this.cs.beats.length}`
    this.textEl.textContent = ''
    this.titleCardsEl.replaceChildren()
    for (const [index, card] of (beat.titleCards ?? []).entries()) {
      const line = document.createElement('div')
      line.className = index === 0 ? 'cutscene-title-card-main' : 'cutscene-title-card-sub'
      line.textContent = card
      this.titleCardsEl.appendChild(line)
    }
    this.titleCardsEl.classList.toggle('is-hidden', !beat.titleCards?.length || beat.shot === 'ending-5b')
    this.hintEl.style.opacity = '0.25'
    this.speakCurrentBeat()
    this.beatDuration = this.visualDuration(beat)
  }

  private speakCurrentBeat(): void {
    if (!this.cs) return
    const dialogue = this.currentBeat.dialogue
    if (dialogue) {
      const id = cutsceneVoiceClipId(this.cs.id, this.beatIdx, dialogue.speaker)
      const clip = verifiedCutsceneVoiceClipUrl(id, dialogue.speaker, dialogue.text)
      this.el.dataset.recordingStatus = clip ? 'ready' : 'pending'
      this.voice.speak(dialogue.speaker, dialogue.text, clip, clip ? LIP_SYNC[id] : undefined)
    }
    else { this.voice.stop(); this.el.dataset.recordingStatus = 'action' }
    this.updateVoiceButton(true)
  }

  private updateVoiceButton(force = false): void {
    const state = this.voice.currentState
    const source = this.voice.currentSource
    const signature = `${state}:${source}:${this.voice.isMuted}`
    if (!force && signature === this.shownVoiceSignature) return
    this.shownVoiceSignature = signature
    this.el.dataset.voiceState = state
    this.el.dataset.voiceSource = source
    this.voiceBtn.dataset.state = state
    this.voiceBtn.textContent = this.voice.isMuted ? '🔇  SOUND OFF' : this.el.dataset.recordingStatus === 'pending' ? '🔊  VOICE PREVIEW' : '🔊  SOUND ON'
    this.voiceBtn.setAttribute('aria-label', this.voice.isMuted ? 'Turn cutscene sound on' : 'Mute cutscene sound')
    this.voiceBtn.setAttribute('aria-pressed', String(this.voice.isMuted))
  }

  private syncDetailDiagnostics(force = false): void {
    const stats = this.stage.detailStats
    const signature = `${stats.tier}:${stats.visibleElements}:${stats.maxElements}`
    if (!force && signature === this.shownDetailSignature) return
    this.shownDetailSignature = signature
    this.el.dataset.qualityTier = String(stats.tier)
    this.el.dataset.detailMultiplier = stats.capacityMultiplier.toFixed(2)
    this.el.dataset.detailElements = String(stats.maxElements)
    this.el.dataset.visibleDetailElements = String(stats.visibleElements)
    this.el.dataset.instancedDetailCalls = String(stats.instancedDrawCalls)
  }

  private typedLen(text: string): number {
    if (this.reduceMotion || this.textRevealed) return text.length
    return Math.min(text.length, Math.floor(this.beatElapsed * TYPE_CPS))
  }

  private visualDuration(beat: Beat): number {
    if (!beat.dialogue) return beat.duration ?? ACTION_HOLD
    const id = this.cs ? cutsceneVoiceClipId(this.cs.id, this.beatIdx, beat.dialogue.speaker) : ''
    return Math.max(2.8, this.voice.recordedDuration ?? LIP_SYNC[id]?.duration ?? beat.dialogue.text.length / TYPE_CPS + READ_HOLD)
  }

  private onTap(): void {
    if (!this.cs || !this.playing || this.waitingForAssets) return
    const dialogue = this.currentBeat.dialogue
    if (!dialogue) {
      this.advance()
      return
    }
    if (this.typedLen(dialogue.text) < dialogue.text.length) {
      this.textRevealed = true
    } else {
      this.advance()
    }
  }

  private advance(): void {
    if (!this.cs) return
    if (this.beatIdx >= this.cs.beats.length - 1) {
      this.finish()
      return
    }
    this.beatIdx++
    this.startBeat()
    diag().cutscene.beat = this.beatIdx
  }

  private finish(): void {
    if (!this.playing) return
    this.playing = false
    this.voice.stop()
    this.sound.stop()
    this.updateVoiceButton(true)
    this.stage.clear()
    this.el.classList.add('is-hidden')
    this.el.style.display = 'none'
    diag().cutscene = { id: '', beat: 0, beats: 0, playing: false }
    const callback = this.onDone
    this.onDone = null
    this.cs = null
    if (callback) callback()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.onDone = null
    this.finish()
    this.sound.dispose()
    this.stage.dispose()
    window.removeEventListener('keydown', this.onKey)
    this.el.remove()
  }

  private onAssetsChanged(state: DioramaAssetState): void {
    if (this.disposed) return
    this.el.dataset.assetsReady = String(state.ready)
    this.el.dataset.failedAssets = state.failed.join(',')
    this.loadingEl.classList.toggle('is-hidden', state.ready)
    if (state.ready && this.playing && this.waitingForAssets) {
      this.waitingForAssets = false
      this.startBeat()
    }
  }
}
